import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { name: '3D Hub', path: '/3d-homepage-hub', icon: 'Home' },
    { name: 'Portfolio', path: '/interactive-portfolio-gallery', icon: 'Briefcase' },
    { name: 'Process', path: '/process-laboratory', icon: 'Beaker' },
    { name: 'Skills', path: '/skill-constellation', icon: 'Star' },
    { name: 'Contact', path: '/contact-nexus', icon: 'MessageCircle' }
  ];

  const secondaryItems = [
    { name: 'Playground', path: '/experimental-playground', icon: 'Zap' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (path) => {
    window.location.href = path;
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => location?.pathname === path;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'glass-panel shadow-cosmic' : 'bg-transparent'
    }`}>
      <div className="w-full px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={() => handleNavClick('/3d-homepage-hub')}
              className="flex items-center space-x-3 group focus-cosmic"
            >
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-accent to-cosmic-energy rounded-lg flex items-center justify-center cosmic-glow-hover">
                  <Icon name="Zap" size={24} className="text-background" />
                </div>
                <div className="absolute -inset-1 bg-gradient-to-r from-accent to-cosmic-energy rounded-lg blur opacity-30 group-hover:opacity-60 transition-opacity"></div>
              </div>
              <div className="hidden sm:block">
                <h1 className="font-headline font-bold text-xl text-foreground group-hover:text-accent transition-colors">
                  3D Portfolio
                </h1>
                <p className="font-body text-xs text-muted-foreground -mt-1">
                  By Amit Pandey
                </p>
              </div>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavClick(item?.path)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-body font-medium transition-all duration-200 focus-cosmic ${
                  isActivePath(item?.path)
                    ? 'bg-accent/20 text-accent border border-accent/30' :'text-muted-foreground hover:text-foreground hover:bg-surface/50'
                }`}
              >
                <Icon name={item?.icon} size={18} />
                <span>{item?.name}</span>
              </button>
            ))}

            {/* More Menu */}
            <div className="relative group">
              <button className="flex items-center space-x-2 px-4 py-2 rounded-lg font-body font-medium text-muted-foreground hover:text-foreground hover:bg-surface/50 transition-all duration-200 focus-cosmic">
                <Icon name="MoreHorizontal" size={18} />
                <span>More</span>
              </button>
              
              <div className="absolute right-0 top-full mt-2 w-48 glass-panel rounded-lg shadow-cosmic-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-60">
                <div className="py-2">
                  {secondaryItems?.map((item) => (
                    <button
                      key={item?.path}
                      onClick={() => handleNavClick(item?.path)}
                      className={`w-full flex items-center space-x-3 px-4 py-2 text-left font-body transition-colors ${
                        isActivePath(item?.path)
                          ? 'text-accent bg-accent/10' :'text-muted-foreground hover:text-foreground hover:bg-surface/30'
                      }`}
                    >
                      <Icon name={item?.icon} size={16} />
                      <span>{item?.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => handleNavClick('/contact-nexus')}
              className="border-accent/30 text-accent hover:bg-accent/10"
            >
              Let's Collaborate
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface/50 transition-colors focus-cosmic"
          >
            <Icon name={isMenuOpen ? "X" : "Menu"} size={24} />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 glass-panel border-t border-border/20 shadow-cosmic-lg">
            <div className="px-6 py-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavClick(item?.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-body font-medium transition-all duration-200 focus-cosmic ${
                    isActivePath(item?.path)
                      ? 'bg-accent/20 text-accent border border-accent/30' :'text-muted-foreground hover:text-foreground hover:bg-surface/50'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.name}</span>
                </button>
              ))}
              
              {secondaryItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavClick(item?.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg font-body font-medium transition-all duration-200 focus-cosmic ${
                    isActivePath(item?.path)
                      ? 'bg-accent/20 text-accent border border-accent/30' :'text-muted-foreground hover:text-foreground hover:bg-surface/50'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.name}</span>
                </button>
              ))}

              <div className="pt-4 border-t border-border/20">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => handleNavClick('/contact-nexus')}
                  className="border-accent/30 text-accent hover:bg-accent/10"
                >
                  Let's Collaborate
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Cosmic Particles Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="particle animate-particle-float" style={{ left: '10%', animationDelay: '0s' }}></div>
        <div className="particle animate-particle-float" style={{ left: '30%', animationDelay: '2s' }}></div>
        <div className="particle animate-particle-float" style={{ left: '50%', animationDelay: '4s' }}></div>
        <div className="particle animate-particle-float" style={{ left: '70%', animationDelay: '6s' }}></div>
        <div className="particle animate-particle-float" style={{ left: '90%', animationDelay: '8s' }}></div>
      </div>
    </header>
  );
};

export default Header;